// =============================================================
// PHONE VERIFICATION — admin-side "is this a real number?" check
// =============================================================
//
// GDPR DECISION (documented here so the rationale lives with the code):
//   Gone Fishing does NOT collect a phone number for the purpose of contacting
//   people by SMS, and we deliberately do NOT run consumer SMS/OTP verification.
//   The phone number is used only as a lightweight account identifier so a
//   returning member can be recognised. New-drop updates are delivered through
//   in-app/browser notifications and Instagram — never SMS. Under GDPR data
//   minimisation (Art. 5(1)(c)), sending a verification SMS would require
//   collecting and processing the number for a new purpose (telephony) plus a
//   lawful basis and an SMS processor — none of which the product needs.
//
//   Conclusion: NO consumer SMS verification is required. Instead, staff can
//   confirm a number is well-formed and plausibly real using the structural
//   checks below when verifying a member in the admin. This is a deterministic,
//   offline heuristic (no third-party lookup, no PII leaves the device).

/** Normalise a free-form phone string into E.164 for Swedish mobiles. */
export function toE164(phone: string): string {
  let cleaned = (phone || '').replace(/[^\d+]/g, '')
  if (!cleaned) return ''
  if (cleaned.startsWith('00')) cleaned = '+' + cleaned.slice(2)
  if (cleaned.startsWith('07')) cleaned = '+46' + cleaned.slice(1)
  else if (cleaned.startsWith('46') && !cleaned.startsWith('+'))
    cleaned = '+' + cleaned
  else if (!cleaned.startsWith('+'))
    cleaned = '+46' + cleaned.replace(/^0+/, '')
  return cleaned
}

export interface PhoneCheck {
  /** Structurally valid AND plausibly real. */
  valid: boolean
  /** The normalised E.164 form. */
  e164: string
  /** Human-readable reason shown to staff. */
  reason: string
  /** Detected country, when recognisable. */
  country: 'SE' | 'unknown'
}

/**
 * Deterministic realness heuristic for Swedish mobile numbers. Catches the
 * common ways a number is fake or mistyped: wrong length, repeated/sequential
 * digits, obviously invalid prefix. Returns a clear verdict + reason.
 */
export function checkPhone(phone: string): PhoneCheck {
  const e164 = toE164(phone)
  if (!e164 || e164.replace(/\D/g, '').length < 8) {
    return {
      valid: false,
      e164,
      reason: 'Too short to be a real number.',
      country: 'unknown',
    }
  }

  // Swedish mobile: +46 7X XXX XX XX → +46 followed by 7 and 8 more digits.
  const isSwedishMobile = /^\+46[7]\d{8}$/.test(e164)
  if (!isSwedishMobile) {
    return {
      valid: false,
      e164,
      reason: 'Not a valid Swedish mobile number (+46 7X XXX XX XX).',
      country: e164.startsWith('+46') ? 'SE' : 'unknown',
    }
  }

  const digits = e164.replace(/\D/g, '') // 467XXXXXXXX
  const subscriber = digits.slice(2) // 7XXXXXXXX

  // Reject all-identical digits (e.g. 0700000000 / 0712121212 patterns).
  if (/^(\d)\1+$/.test(subscriber)) {
    return {
      valid: false,
      e164,
      reason: 'Looks fake — all digits are identical.',
      country: 'SE',
    }
  }

  // Reject simple ascending/descending runs (e.g. 0701234567).
  const ascending = '0123456789'
  const descending = '9876543210'
  const tail = subscriber.slice(1) // drop the leading 7
  if (ascending.includes(tail) || descending.includes(tail)) {
    return {
      valid: false,
      e164,
      reason: 'Looks fake — sequential digit run.',
      country: 'SE',
    }
  }

  return {
    valid: true,
    e164,
    reason: 'Valid, plausibly real Swedish mobile.',
    country: 'SE',
  }
}
